import React, { useEffect, useState } from 'react';
import { 
  Card, 
  CardBody, 
  CardHeader, 
  Switch, 
  Spinner, 
  Button,
  Divider,
  Chip
} from "@heroui/react";
import { useEntityFields } from 'shared/contexts/EntityFieldContext';
import { EntityFieldConfig } from 'shared/translations/entity-field-translations';

interface EntityFieldConfigPanelProps {
  className?: string;
}

const EntityFieldConfigPanel: React.FC<EntityFieldConfigPanelProps> = ({ className }) => {
  const { 
    fieldConfig, 
    updateFieldConfig, 
    loadFieldConfig,
    isLoading 
  } = useEntityFields();
  
  const [localConfig, setLocalConfig] = useState<EntityFieldConfig>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Initialize local config when fieldConfig loads
  useEffect(() => {
    // Only update if we don't have unsaved changes OR if we just saved (expected update)
    if (!hasChanges || justSaved) {
      setLocalConfig(fieldConfig);
      if (justSaved) {
        setJustSaved(false);
        setHasChanges(false);
      }
    }
  }, [fieldConfig, hasChanges, justSaved]);

  // Handle field toggle
  const handleFieldToggle = (entityType: string, fieldName: string, enabled: boolean) => {
    // Deep clone to avoid mutation issues
    const updatedConfig = JSON.parse(JSON.stringify(localConfig));
    
    if (updatedConfig[entityType]?.fields[fieldName]) {
      updatedConfig[entityType].fields[fieldName].emailEnabled = enabled;
      
      setLocalConfig(updatedConfig);
      setHasChanges(true);
      setError(null);
      setSuccessMessage(null);
    }
  };

  // Save changes
  const handleSave = async () => {
    if (!hasChanges) return;
    
    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);
    
    try {
      await updateFieldConfig(localConfig);
      
      // Signal that we expect the context to update with fresh data
      setJustSaved(true);
      
      setSuccessMessage('Email field configuratie succesvol opgeslagen!');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Onbekende fout';
      setError(`Fout bij opslaan: ${errorMessage}`);
    } finally {
      setIsSaving(false);
    }
  };

  // Reset changes
  const handleReset = () => {
    setLocalConfig(fieldConfig);
    setHasChanges(false);
    setError(null);
    setSuccessMessage(null);
  };

  // Refresh from backend
  const handleRefresh = async () => {
    setError(null);
    setSuccessMessage(null);
    await loadFieldConfig();
  };

  if (isLoading && Object.keys(fieldConfig).length === 0) {
    return (
      <Card className={className}>
        <CardBody className="flex items-center justify-center py-12">
          <Spinner size="lg" label="Bezig met laden van veld configuratie..." />
        </CardBody>
      </Card>
    );
  }

  const entityTypes = Object.keys(localConfig);
  const totalFields = entityTypes.reduce((sum, entityType) => 
    sum + Object.keys(localConfig[entityType]?.fields || {}).length, 0
  );
  const enabledFields = entityTypes.reduce((sum, entityType) => 
    sum + Object.values(localConfig[entityType]?.fields || {}).filter(field => field.emailEnabled).length, 0
  );

  return (
    <div className={`space-y-6 ${className || ''}`}>
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Email Veld Configuratie</h2>
          <p className="text-gray-600 mt-1">
            Configureer welke velden beschikbaar zijn voor dynamische email waarden
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="light"
            onPress={handleRefresh}
            isDisabled={isLoading}
          >
            Vernieuwen
          </Button>
          {hasChanges && (
            <Button
              variant="light"
              onPress={handleReset}
              isDisabled={isSaving}
            >
              Annuleren
            </Button>
          )}
          <Button
            color="primary"
            onPress={handleSave}
            isDisabled={!hasChanges || isSaving}
            isLoading={isSaving}
          >
            {isSaving ? 'Opslaan...' : 'Opslaan'}
          </Button>
        </div>
      </div>

      {/* Summary */}
      <Card>
        <CardBody className="flex flex-row items-center justify-between">
          <div className="flex gap-4">
            <Chip variant="flat" color="primary">
              {entityTypes.length} Entiteiten
            </Chip>
            <Chip variant="flat" color="success">
              {enabledFields} / {totalFields} Velden Ingeschakeld
            </Chip>
          </div>
          {hasChanges && (
            <Chip variant="flat" color="warning">
              Niet-opgeslagen wijzigingen
            </Chip>
          )}
        </CardBody>
      </Card>

      {/* Messages */}
      {error && (
        <Card className="border-l-4 border-danger">
          <CardBody className="bg-danger-50">
            <div className="flex items-start">
              <div className="text-danger-500 mr-2">⚠️</div>
              <div>
                <p className="font-medium text-danger-700">Fout</p>
                <p className="text-sm text-danger-600">{error}</p>
              </div>
            </div>
          </CardBody>
        </Card>
      )}
      
      {successMessage && (
        <Card className="border-l-4 border-success">
          <CardBody className="bg-success-50">
            <div className="flex items-start">
              <div className="text-success-500 mr-2">✅</div>
              <div>
                <p className="font-medium text-success-700">Succes</p>
                <p className="text-sm text-success-600">{successMessage}</p>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Entity Configuration Cards */}
      <div className="grid gap-6">
        {entityTypes.map((entityType) => {
          const entityConfig = localConfig[entityType];
          if (!entityConfig) return null;

          const fields = Object.entries(entityConfig.fields);
          const entityEnabledCount = fields.filter(([_, field]) => field.emailEnabled).length;

          return (
            <Card key={entityType}>
              <CardHeader className="pb-2">
                <div className="flex justify-between items-center w-full">
                  <div>
                    <h3 className="text-lg font-semibold capitalize">{entityConfig.displayName}</h3>
                    <p className="text-sm text-gray-500">
                      {entityEnabledCount} van {fields.length} velden ingeschakeld
                    </p>
                  </div>
                  <Chip 
                    variant="flat" 
                    color={entityEnabledCount > 0 ? "success" : "default"}
                    size="sm"
                  >
                    {entityType}
                  </Chip>
                </div>
              </CardHeader>
              <Divider />
              <CardBody className="pt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {fields.map(([fieldName, fieldConfig]) => (
                    <div 
                      key={fieldName}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {fieldConfig.dutchName}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {fieldName} ({fieldConfig.type})
                        </p>
                      </div>
                      <Switch
                        key={`${entityType}-${fieldName}-${fieldConfig.emailEnabled}`}
                        size="sm"
                        isSelected={fieldConfig.emailEnabled}
                        onValueChange={(enabled) => handleFieldToggle(entityType, fieldName, enabled)}
                        color="success"
                      />
                    </div>
                  ))}
                </div>
              </CardBody>
            </Card>
          );
        })}
      </div>

      {entityTypes.length === 0 && (
        <Card>
          <CardBody className="text-center py-12">
            <p className="text-gray-500">
              Geen entiteit configuraties gevonden. Klik op "Vernieuwen" om opnieuw te proberen.
            </p>
          </CardBody>
        </Card>
      )}
    </div>
  );
};

export default EntityFieldConfigPanel; 